import WaIcon from "@awesome.me/webawesome-pro/dist/react/icon/index.js";
import WaInput from "@awesome.me/webawesome-pro/dist/react/input/index.js";
import type { FeatureCollection } from "geojson";
import React, { useEffect, useRef, useState } from "react";

import { ensureGooglePlaces } from "../lib/googleMaps";
import type { LatLng } from "../types/journey";
import { isPointInsideBoundary } from "./JourneyMap/helpers/camera";

type Picked = { label: string; location: LatLng };

type Props = {
  label: string;
  placeholder?: string;
  value: string;
  onValueChange: (v: string) => void;
  onPick: (picked: Picked) => void;
  disabled?: boolean;
  errorText?: string | null;

  countryCode?: "gb";

  boundary?: FeatureCollection | null;
  enforceBoundary?: boolean;
  outsideBoundaryMessage?: string;
  onOutsideBoundaryPick?: (message: string) => void;

  /** If true, biases autocomplete results toward the BCP boundary (no strict bounds). */
  biasToBoundary?: boolean;

  onBeginEdit?: () => void;
  onEndEdit?: () => void;
};

type InputWithAutocomplete = HTMLInputElement & {
  __gPlacesAutocomplete?: google.maps.places.Autocomplete;
  __gPlacesListener?: { remove: () => void } | google.maps.MapsEventListener;
};

// Module-scoped flag so BOTH address fields can see it.
let pacPointerActive = false;
let pacClearTimer: number | null = null;

function geojsonBbox(
  fc: FeatureCollection,
): [minLng: number, minLat: number, maxLng: number, maxLat: number] | null {
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;

  const walk = (c: any) => {
    if (!c) return;

    // hit a coordinate pair: [lng, lat]
    if (typeof c[0] === "number" && typeof c[1] === "number") {
      const lng = c[0];
      const lat = c[1];

      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      return;
    }

    for (const child of c) walk(child);
  };

  for (const f of fc.features ?? []) {
    if (!f.geometry) continue;
    walk((f.geometry as any).coordinates);
  }

  if (!Number.isFinite(minLng)) return null;
  return [minLng, minLat, maxLng, maxLat];
}

function bboxCenter(b: [number, number, number, number]) {
  const [minLng, minLat, maxLng, maxLat] = b;
  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
}

// Small bias window around center (tweak if you want wider/narrower bias)
function boundsAroundCenter(
  g: typeof google,
  center: { lat: number; lng: number },
  padLat = 0.12,
  padLng = 0.18,
) {
  return new g.maps.LatLngBounds(
    new g.maps.LatLng(center.lat - padLat, center.lng - padLng),
    new g.maps.LatLng(center.lat + padLat, center.lng + padLng),
  );
}

export const GoogleAddressFinder: React.FC<Props> = ({
  label,
  placeholder = "Search an address or postcode…",
  value,
  onValueChange,
  onPick,
  disabled,
  errorText,
  countryCode = "gb",
  boundary,
  enforceBoundary = false,
  outsideBoundaryMessage = "Selected location is outside the allowed area.",
  onOutsideBoundaryPick,
  biasToBoundary = false,
  onBeginEdit,
  onEndEdit,
}) => {
  const waRef = useRef<any>(null);

  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const editingRef = useRef(false);

  const beginEditOnce = () => {
    if (editingRef.current) return;
    editingRef.current = true;
    onBeginEdit?.();
  };

  const endEdit = () => {
    if (!editingRef.current) return;
    editingRef.current = false;
    onEndEdit?.();
  };

  const handleInput = (e: any) => {
    beginEditOnce();

    const next = String(e?.target?.value ?? "");
    setDraft(next);

    queueMicrotask(() => onValueChange(next));
  };

  const latest = useRef({
    boundary,
    enforceBoundary,
    outsideBoundaryMessage,
    onOutsideBoundaryPick,
    onPick,
    onValueChange,
    onEndEdit,
    biasToBoundary,
  });

  useEffect(() => {
    latest.current = {
      boundary,
      enforceBoundary,
      outsideBoundaryMessage,
      onOutsideBoundaryPick,
      onPick,
      onValueChange,
      onEndEdit,
      biasToBoundary,
    };
  }, [
    boundary,
    enforceBoundary,
    outsideBoundaryMessage,
    onOutsideBoundaryPick,
    onPick,
    onValueChange,
    onEndEdit,
    biasToBoundary,
  ]);

  const deferredCleanupTimer = useRef<number | null>(null);

  /**
   * PAC guard (selection-safe):
   * - DO NOT preventDefault
   * - DO NOT blur/focus anything during selection
   *
   * We only set a short-lived global flag while the user is clicking a suggestion,
   * so other fields can ignore onFocus/onBlur side-effects (clearing/resetting).
   */
  useEffect(() => {
    const clearSoon = () => {
      // Keep the flag alive a tiny bit past mouseup so React focus handlers
      // on underlying elements don’t fire destructive "begin edit" logic.
      if (pacClearTimer != null) window.clearTimeout(pacClearTimer);
      pacClearTimer = window.setTimeout(() => {
        pacPointerActive = false;
        pacClearTimer = null;
      }, 50);
    };

    const onPointerDownCapture = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const inPac = Boolean(target.closest(".pac-container"));
      if (!inPac) return;

      pacPointerActive = true;

      // cancel any pending clear
      if (pacClearTimer != null) {
        window.clearTimeout(pacClearTimer);
        pacClearTimer = null;
      }
    };

    document.addEventListener("mousedown", onPointerDownCapture as any, true);
    document.addEventListener("touchstart", onPointerDownCapture as any, true);

    document.addEventListener("mouseup", clearSoon, true);
    document.addEventListener("touchend", clearSoon, true);
    document.addEventListener("touchcancel", clearSoon, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDownCapture as any, true);
      document.removeEventListener("touchstart", onPointerDownCapture as any, true);

      document.removeEventListener("mouseup", clearSoon, true);
      document.removeEventListener("touchend", clearSoon, true);
      document.removeEventListener("touchcancel", clearSoon, true);

      if (pacClearTimer != null) {
        window.clearTimeout(pacClearTimer);
        pacClearTimer = null;
      }
    };
  }, []);

  useEffect(() => {
    const waEl = waRef.current as HTMLElement | null;
    if (!waEl) return;

    let cancelled = false;

    const clearDeferredCleanup = () => {
      if (deferredCleanupTimer.current != null) {
        window.clearTimeout(deferredCleanupTimer.current);
        deferredCleanupTimer.current = null;
      }
    };

    const attach = () => {
      if (cancelled) return;

      const internalInput = (waEl as any).shadowRoot?.querySelector?.(
        "input",
      ) as InputWithAutocomplete | null;

      if (!internalInput) {
        requestAnimationFrame(attach);
        return;
      }

      const g = window.google;
      if (!g?.maps?.places) {
        requestAnimationFrame(attach);
        return;
      }

      clearDeferredCleanup();

      internalInput.setAttribute("autocomplete", "off");

      if (!internalInput.__gPlacesAutocomplete) {
        internalInput.__gPlacesAutocomplete = new g.maps.places.Autocomplete(internalInput, {
          fields: ["geometry", "formatted_address", "name", "address_components"],
          componentRestrictions: { country: countryCode },
        });
      } else {
        // keep country restriction in sync if countryCode changes
        internalInput.__gPlacesAutocomplete.setOptions({
          componentRestrictions: { country: countryCode },
        });
      }

      // ✅ Bias results toward the boundary (BCP), without strict bounds.
      if (latest.current.biasToBoundary && latest.current.boundary) {
        const bbox = geojsonBbox(latest.current.boundary);
        if (bbox) {
          const center = bboxCenter(bbox);
          const biasBounds = boundsAroundCenter(g, center);
          internalInput.__gPlacesAutocomplete.setBounds(biasBounds);
          internalInput.__gPlacesAutocomplete.setOptions({ strictBounds: false });
        }
      } else {
        // Clear bias if previously set
        internalInput.__gPlacesAutocomplete.setBounds(undefined as any);
        internalInput.__gPlacesAutocomplete.setOptions({ strictBounds: false });
      }

      internalInput.__gPlacesListener?.remove?.();

      internalInput.__gPlacesListener = internalInput.__gPlacesAutocomplete.addListener(
        "place_changed",
        () => {
          const place = internalInput.__gPlacesAutocomplete?.getPlace?.();
          const loc = place?.geometry?.location;
          if (!loc) return;

          const labelText = place.formatted_address || place.name || internalInput.value || "";

          const picked: Picked = {
            label: labelText,
            location: { lat: loc.lat(), lng: loc.lng() },
          };

          setDraft(labelText);
          latest.current.onValueChange(labelText);

          if (latest.current.enforceBoundary && latest.current.boundary) {
            const ok = isPointInsideBoundary(latest.current.boundary, picked.location);
            if (!ok) {
              latest.current.onOutsideBoundaryPick?.(latest.current.outsideBoundaryMessage);
              return;
            }
          }

          latest.current.onPick(picked);

          latest.current.onEndEdit?.();
          editingRef.current = false;
        },
      );
    };

    (async () => {
      try {
        await ensureGooglePlaces();
        if (cancelled) return;
        attach();
      } catch {
        // silent
      }
    })();

    return () => {
      cancelled = true;

      deferredCleanupTimer.current = window.setTimeout(() => {
        const internalInput = (waEl as any).shadowRoot?.querySelector?.(
          "input",
        ) as InputWithAutocomplete | null;

        if (!internalInput) return;

        internalInput.__gPlacesListener?.remove?.();
        internalInput.__gPlacesListener = undefined;
      }, 0);
    };
  }, [countryCode, boundary, biasToBoundary]);

  return (
    <div className="wa-stack wa-gap-2xs">
      <WaInput
        type="search"
        ref={waRef}
        label={label}
        placeholder={placeholder}
        value={draft}
        disabled={disabled}
        data-invalid={errorText ? "true" : "false"}
        onInput={handleInput as any}
        onFocus={() => {
          // Don’t trigger destructive "begin edit" side-effects if focus happened
          // due to a PAC click overlapping another field.
          if (pacPointerActive) return;
          beginEditOnce();
        }}
        onBlur={() => {
          // Don’t end edit mode mid-PAC selection.
          if (pacPointerActive) return;
          endEdit();
        }}
      >
        <WaIcon slot="start" name="magnifying-glass" />
      </WaInput>

      {errorText && (
        <span
          className="wa-caption-s"
          style={{ color: "var(--wa-color-danger-60)", pointerEvents: "none" }}
        >
          {errorText}
        </span>
      )}
    </div>
  );
};
